/**
 * ForgeTryonElement — the `<forge-tryon>` custom element.
 *
 * Registers as a Web Component with Shadow DOM. Heavy dependencies (camera,
 * tracking, renderer) are lazy-loaded only when the user opens the modal.
 */

import { buildConfig } from './config.js';
import type { ForgeTryonConfig } from './config.js';
import { addToCart } from './cart-bridge.js';
import type { CartBridgeOptions } from './cart-bridge.js';
// --- @mirrably/core imports (camera, analytics, events, data, UI) ---
import { EventBus } from '@mirrably/core';
import { Analytics, createGA4Callback } from '@mirrably/core';
import { CameraManager } from '@mirrably/core';
import type { CameraError } from '@mirrably/core';
import type { MediaPort } from '@mirrably/core';
import { PerfRecorder } from '@mirrably/core';
import type { PerfSnapshot } from '@mirrably/core';
import { checkCameraPermission, requestCameraPermission } from '@mirrably/core';
import type { MeshMaterialOverride } from '@mirrably/core';
import {
  createTriggerButton,
  setTriggerButtonDisabled,
  createModal,
  showModal,
  hideModal,
  showStateScreen,
  hideStateScreen,
  updateProductInfo,
  CatalogSlider,
} from '@mirrably/core/ui';
import type { ModalElements, PDLandmarkProvider, PDMeasurePayload } from '@mirrably/core/ui';
// --- @mirrably/tracking imports ---
import { MediaPipeTracker, PD_MAX_MM, PD_MIN_MM } from '@mirrably/tracking';
import type { EyewearLandmarks } from '@mirrably/tracking';
// --- Local (eyewear-specific) imports ---
import { TryonScene } from '../renderer/scene.js';
import type { RenderSnapshot } from '../renderer/scene.js';
import { FaceOccluder } from '../renderer/occluder.js';
import { GlassesManager } from '../renderer/glasses.js';
import { MaterialsManager } from '../renderer/materials.js';
import { t } from '../i18n/strings.js';
import { DebugOverlay } from '../renderer/debug-overlay.js';
import { captureSnapshot, createSnapshotToastHandler } from './snapshot-service.js';
import {
  loadProduct as loadProductManaged,
  loadCatalog as loadCatalogManaged,
} from './product-manager.js';
import type { ProductManagerContext } from './product-manager.js';
import { wireModalHandlers, createCartToastHandler, switchModeDOM, loadPhotoFile as loadPhotoFileManaged } from './modal-controller.js';

// Inline the CSS as a string so it can be injected into the Shadow DOM.
// Vite handles this via the ?inline import suffix at build/dev time.
// In test environments (Vitest) the import resolves to an empty string
// because CSS files are treated as empty asset modules by the test runner.
// @ts-expect-error — CSS module types are not generated; Vite resolves this at build time.
import STYLES from '../ui/styles.css?inline';

/** Attributes observed by the custom element. */
const OBSERVED_ATTRS = [
  'product-id',
  'store-id',
  'api-url',
  'api-key',
  'theme',
  'locale',
  'analytics',
  'debug',
  'cart-url',
  'cart-platform',
  'test-video-src',
  'ipd-scale-factor',
  'use-pd-calibration',
  'reference-pd-mm',
] as const;
type ObservedAttr = (typeof OBSERVED_ATTRS)[number];

const PD_STORAGE_KEY = 'mirrably:calib_pdMm';
const DEFAULT_REFERENCE_PD_MM = 63;

export class ForgeTryonElement extends HTMLElement {
  // ── Static Web Components API ──────────────────────────────────────────────

  static get observedAttributes(): string[] {
    return [...OBSERVED_ATTRS];
  }

  // ── Instance state ─────────────────────────────────────────────────────────

  private readonly shadow: ShadowRoot;
  private readonly bus: EventBus;
  private config: ForgeTryonConfig;

  /**
   * Pluggable analytics instance.
   *
   * Exposed publicly so host pages can attach custom callbacks:
   * ```js
   * document.querySelector('forge-tryon').analytics.addCallback(myFn);
   * ```
   */
  readonly analytics: Analytics;

  /** The trigger button rendered in the light DOM slot. */
  private triggerBtn: HTMLButtonElement | null = null;
  /** Modal element references — only present while modal is open. */
  private modal: ModalElements | null = null;
  /** Camera manager — created on open, kept warm across re-opens. */
  private camera: CameraManager | null = null;
  /** Face tracker — lazy-initialised once, kept warm for re-open. */
  private tracker: MediaPipeTracker | null = null;
  /** Three.js scene — lazy-initialised once, kept warm for re-open. */
  private scene: TryonScene | null = null;
  /** Face occluder — created once scene is initialised. */
  private occluder: FaceOccluder | null = null;
  /** Debug wireframe overlay — shows 478 landmark dots when debug=true. */
  private debugOverlay: DebugOverlay | null = null;
  /** Glasses model manager — lives alongside the scene. */
  private glasses: GlassesManager | null = null;
  /** Materials manager — lives alongside the scene. */
  private materials: MaterialsManager | null = null;
  /** CatalogSlider — created when the modal opens, destroyed on close. */
  private catalog: CatalogSlider | null = null;
  /** Monotonic guard so stale product fetches cannot enqueue stale model swaps. */
  private productLoadGeneration = 0;
  /** Whether the modal is currently visible. */
  private isOpen = false;
  /**
   * Optional MediaPort injected by the host page (or test harness) before the
   * widget opens. When set, CameraManager and requestCameraPermission use this
   * port instead of the default BrowserMediaPort. Null means production default.
   *
   * Implemented as a prototype accessor (get/set backed by `_mediaPort`) rather
   * than a class-field so that pre-upgrade own-property assignments survive the
   * custom-element upgrade cycle. When a host page does:
   *
   * ```js
   * el.mediaPort = new TestVideoMediaPort('/test-loop.mp4');
   * ```
   *
   * …before the CDN bundle loads and `customElements.define('forge-tryon', …)`
   * upgrades the element, the value is stored as an own-data-property on the
   * base HTMLElement. A class-field initializer (`mediaPort = null`) in the
   * constructor would clobber that value. The accessor lives on the prototype,
   * not the instance, so it cannot be overwritten by the constructor. The
   * `_upgradeProperty('mediaPort')` call in `connectedCallback` then detects the
   * pre-set own-data-property, deletes it, and re-routes the value through this
   * setter into `_mediaPort`.
   *
   * See: https://web.dev/custom-elements-best-practices/#make-properties-lazy
   */
  private _mediaPort: MediaPort | null = null;
  get mediaPort(): MediaPort | null { return this._mediaPort; }
  set mediaPort(v: MediaPort | null) { this._mediaPort = v; }
  /** Active variant name — used to re-apply materialOverrides on variant switch. */
  private activeVariantName: string | null = null;
  /** Whether the WebGL context was lost on the previous frame (Safari recovery). */
  private wasContextLost = false;
  /** requestAnimationFrame handle for the tracking loop. */
  private trackingLoopId: number | null = null;
  /** Whether tracking was active on the previous frame (for tracking-lost detection). */
  private wasTracking = false;
  /** Active PD measurement sampler registered by the shared modal. */
  private pdSampleCallback: Parameters<PDLandmarkProvider>[0] | null = null;
  /** Performance recorder — FPS/latency/jitter instrumentation for the rAF loop. */
  private perf: PerfRecorder | null = null;
  /** ResizeObserver for the canvas — handles window/orientation changes. */
  private resizeObserver: ResizeObserver | null = null;

  /** Timestamp set when the modal opens — used to compute duration_ms on close. */
  private openedAt: number | null = null;

  /** Current try-on mode: live camera or uploaded photo. */
  private tryOnMode: 'live' | 'photo' = 'live';
  /** Last emitted face shape — used to detect shape changes and avoid duplicate events. */
  private _lastFaceShape: string | null = null;
  /** Cart toast handler — manages transient toast UI and timer cleanup. */
  private _cartToast = createCartToastHandler();

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: 'open' });
    this.bus = new EventBus(this, 'forge-tryon');
    this.config = buildConfig({});
    // Analytics is initialised with an empty storeId; connectedCallback will
    // rebuild config and re-register GA4 / onAnalytics once attributes are known.
    this.analytics = new Analytics('');
  }

  // ── Lifecycle callbacks ────────────────────────────────────────────────────

  /**
   * Performs the "upgrade property" dance for properties that callers may set
   * as own-data-properties on a not-yet-upgraded element.
   *
   * When a host page runs `el.mediaPort = port` before `customElements.define`
   * upgrades the element, the value is stored as a raw own-data-property on the
   * base HTMLElement. After upgrade, that own-property shadows the prototype
   * accessor. Calling this method from connectedCallback detects the own-property,
   * deletes it, and re-routes the value through the setter (into `_mediaPort`).
   *
   * This must be called BEFORE reading or writing any instance state that
   * depends on the property (i.e., at the very top of connectedCallback).
   *
   * Reference: https://web.dev/custom-elements-best-practices/#make-properties-lazy
   */
  private _upgradeProperty(name: keyof this): void {
    if (Object.prototype.hasOwnProperty.call(this, name)) {
      const value = this[name];
      delete (this as Record<string, unknown>)[name as string];
      (this as Record<string, unknown>)[name as string] = value;
    }
  }

  connectedCallback(): void {
    // Rescue any pre-upgrade own-property assignments (e.g., host page set
    // el.mediaPort before customElements.define ran). Must run first.
    this._upgradeProperty('mediaPort');
    this.config = buildConfig(this.collectAttributes());
    this.applyAnalyticsConfig();
    this.render();
  }

  disconnectedCallback(): void {
    this.destroy();
  }

  attributeChangedCallback(
    name: ObservedAttr,
    _oldValue: string | null,
    _newValue: string | null,
  ): void {
    // Re-build config whenever a relevant attribute changes
    const updated = buildConfig(this.collectAttributes());
    this.config = updated;

    // Propagate theme change to open modal if present
    if (name === 'theme' && this.modal) {
      this.applyTheme(this.modal.container);
    }

    // Re-apply analytics adapters when analytics-related attributes change
    if (name === 'analytics') {
      this.applyAnalyticsConfig();
    }

    // Toggle debug overlay on/off while widget is open
    if (name === 'debug') {
      void this._syncDebugOverlay();
    }

    if (name === 'ipd-scale-factor' || name === 'use-pd-calibration' || name === 'reference-pd-mm') {
      this.applyFitCalibration();
    }
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  /**
   * Get a snapshot of current perf metrics. Returns null if tracking not yet started.
   *
   * Designed for on-demand polling by the host page:
   * ```js
   * const metrics = widget.getPerfSnapshot();
   * if (metrics) console.log(`FPS: ${metrics.fps.toFixed(1)}`);
   * ```
   */
  getPerfSnapshot(): PerfSnapshot | null {
    return this.perf?.getSnapshot() ?? null;
  }

  /**
   * Get a render-position snapshot for automated fit gates.
   *
   * Returns null until the modal renderer is initialised. This is deliberately
   * read-only and stable so E2E tests and operator tools do not reach into
   * private Three.js scene state.
   */
  getRenderSnapshot(): RenderSnapshot | null {
    return this.scene?.getRenderSnapshot() ?? null;
  }

  /**
   * Open the try-on modal.
   *
   * Safe to call programmatically (e.g. from a product page "Try On" CTA).
   * Lazy-loads the camera, tracker, and renderer on first call; keeps them
   * warm across close/re-open cycles.
   */
  async open(): Promise<void> {
    if (this.isOpen) return;
    this.isOpen = true;

    // Clean up any stale modal from a previous failed attempt (e.g. retry
    // after camera permission denied — the retry callback sets isOpen=false
    // then calls open() again, but the old modal DOM may still be present).
    if (this.modal) {
      const { overlay } = this.modal;
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      this.modal = null;
    }
    this.stopTrackingLoop();

    if (this.triggerBtn) setTriggerButtonDisabled(this.triggerBtn, true);

    // Record open time for duration tracking on close
    this.openedAt = Date.now();

    // Build and mount the modal
    const modal = createModal({
      onClose: () => void this.close(),
      t: (key: string) => t(this.config.locale, key as keyof import('../i18n/strings.js').Messages),
      cssPrefix: 'ft-',
      pdLandmarkProvider: (callback) => this.providePDLandmarks(callback),
      onPDMeasure: (result) => this.handlePDMeasure(result),
      ...(this.mediaPort ? { mediaPort: this.mediaPort } : {}),
    });
    this.modal = modal;
    this.applyTheme(modal.container);
    this.shadow.appendChild(modal.overlay);
    showModal(modal.overlay, 'ft-');

    // Product info bar starts hidden — updated with real name once catalog loads
    // (avoids showing raw product ID to the user)
    modal.productInfoBar.style.display = 'none';

    // Wire up all button handlers via modal-controller
    wireModalHandlers(modal, {
      onSnapshot: () => void this.snapshot(),
      onSwitchCamera: () => void this.camera?.switchCamera(),
      onSwitchMode: (mode) => {
        if (mode !== this.tryOnMode) void this.switchTryOnMode(mode);
      },
      onPhotoFile: (file) => void this.loadPhotoFile(file),
      onAddToCart: () => this._handleAddToCart(),
    });

    // ── Catalog slider ────────────────────────────────────────────────────────
    this.catalog = new CatalogSlider({
      container: modal.catalogStrip,
      cssPrefix: 'ft-',
      onProductSelect: (product) => {
        this.config = { ...this.config, productId: product.id };
        if (this.scene && this.glasses) {
          void this.loadProduct(product.id);
        }
        this.bus.emit('product-change', { productId: product.id });
        this.analytics.trackWidgetOpened(product.id);

        // Update the product info bar with the selected product
        if (this.modal) {
          // price is an optional extension field not yet in the Product type
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const price = (product as any).price as string | undefined;
          updateProductInfo(this.modal.productInfoBar, product.name ?? product.id, price);
        }
      },
      onVariantSelect: (variant) => {
        this.switchVariant(variant.name, variant.materialOverrides);
      },
    });

    // Fetch the full catalog asynchronously and populate the slider
    if (this.config.storeId && this.config.apiUrl) {
      void this._loadCatalog();
    }

    this.bus.emit('open');
    this.analytics.trackWidgetOpened(this.config.productId);

    // Check camera permission before starting
    const permStatus = await checkCameraPermission();

    if (permStatus === 'denied') {
      showStateScreen(
        modal.stateScreen,
        'Camera access blocked',
        'To use virtual try-on, allow camera access in your browser settings and reload the page.',
        'ft-',
      );
      if (this.triggerBtn) setTriggerButtonDisabled(this.triggerBtn, false);
      return;
    }

    // Show loading state while the camera spins up
    showStateScreen(
      modal.stateScreen,
      'Starting camera…',
      'Please allow camera access when prompted.',
      'ft-',
    );

    if (permStatus === 'prompt') {
      const granted = await requestCameraPermission(this.mediaPort ?? undefined);
      if (!granted) {
        showStateScreen(
          modal.stateScreen,
          'Camera access denied',
          'Virtual try-on requires camera access. Grant permission in your browser and try again.',
          'ft-',
          'Retry',
          () => void this.open(),
        );
        this.isOpen = false;
        if (this.triggerBtn) setTriggerButtonDisabled(this.triggerBtn, false);
        return;
      }
    }

    // ── Video source: test video OR live camera ─────────────────────────────
    if (this.config.testVideoSrc) {
      // Test video mode — skip camera permissions, load video from URL
      modal.video.src = this.config.testVideoSrc;
      modal.video.loop = true;
      modal.video.muted = true;
      modal.video.playsInline = true;
      modal.video.crossOrigin = 'anonymous';
      try {
        await modal.video.play();
      } catch {
        // Autoplay may be blocked — user interaction already happened (button click)
        modal.video.play().catch(() => {});
      }
      // Mirror the canvas for consistency with camera mode
      modal.canvas.classList.add('ft-mirrored');
      hideStateScreen(modal.stateScreen);
    } else {
      // Start camera stream — reuse existing CameraManager if available
      if (!this.camera) {
        this.camera = new CameraManager(this.mediaPort ? { mediaPort: this.mediaPort } : undefined);
        // Mirror the AR canvas whenever the video mirror state changes
        this.camera.onMirrorChange = (mirrored: boolean) => {
          this.modal?.canvas.classList.toggle('ft-mirrored', mirrored);
        };
      }
      const camera = this.camera;

      try {
        await camera.start();
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        showStateScreen(
          modal.stateScreen,
          'Camera error',
          msg,
          'ft-',
          'Retry',
          () => void this.open(),
        );
        this.bus.emit('error', err as CameraError);
        this.analytics.trackError(msg, 'camera_start');
        if (this.triggerBtn) setTriggerButtonDisabled(this.triggerBtn, false);
        return;
      }

      // Attach camera video to modal — the video is hidden (opacity: 0) because
      // the Three.js canvas renders it as a VideoTexture. The DOM video element
      // is still needed as the source for both VideoTexture and MediaPipe tracking.
      modal.video.srcObject = camera.stream;
      hideStateScreen(modal.stateScreen);
    }

    // ── Parallel init: tracker + scene (first open only) ───────────────────
    // Camera is already started; init tracker and scene concurrently to cut
    // startup latency (WASM download + WebGL init can overlap).
    const trackerInit = (async () => {
      if (!this.tracker) this.tracker = new MediaPipeTracker();
      if (!this.tracker.isReady) {
        try {
          await this.tracker.init();
        } catch (_err) {
          this.bus.emit('error', _err as Error);
        }
      }
    })();

    const sceneInit = (async () => {
      if (!this.scene) this.scene = new TryonScene();
      if (!this.scene.isInitialized) {
        try {
          const vw = modal.video.videoWidth || 640;
          const vh = modal.video.videoHeight || 480;
          modal.canvas.width = vw;
          modal.canvas.height = vh;
          await this.scene.init(modal.canvas, modal.video);
          this.applyFitCalibration();
          modal.video.style.opacity = '0';

          this.occluder = new FaceOccluder();
          const threeScene = this.scene.getScene();
          if (threeScene) {
            await this.occluder.init(threeScene);
          }
          if (this.config.debug && threeScene) {
            this.debugOverlay = new DebugOverlay();
            await this.debugOverlay.init(threeScene);
          }
          this.glasses = new GlassesManager();
          this.materials = new MaterialsManager(this.scene!);
        } catch (_err) {
          this.bus.emit('error', _err as Error);
        }
      }
    })();

    await Promise.all([trackerInit, sceneInit]);

    // Enable debug landmark output after both tracker and scene are ready
    if (this.config.debug && this.tracker) {
      (this.tracker as MediaPipeTracker).setDebug(true);
    }

    // ── Load product model ────────────────────────────────────────────────────
    if (this.config.productId && this.config.apiUrl && this.scene && this.glasses) {
      void this.loadProduct(this.config.productId);
    }

    // ── Sync canvas size with actual video dimensions ──────────────────────────
    // CRITICAL: The canvas internal resolution must match the video's native
    // aspect ratio, not the CSS layout dimensions. Otherwise the VideoTexture
    // plane will be stretched/distorted because the Three.js camera frustum
    // won't match the video feed's aspect ratio.
    //
    // We use the video's native dimensions for the canvas buffer, and let CSS
    // `width: 100%; height: 100%; object-fit: cover` handle visual fitting.
    {
      const vw = modal.video.videoWidth || 640;
      const vh = modal.video.videoHeight || 480;
      // Use video native resolution (or downscale for performance on hi-res cameras)
      const maxDim = 720;
      const scale = Math.min(1, maxDim / Math.max(vw, vh));
      const canvasW = Math.round(vw * scale);
      const canvasH = Math.round(vh * scale);
      modal.canvas.width = canvasW;
      modal.canvas.height = canvasH;
      this.scene?.resize(canvasW, canvasH);

      // Fix aspect ratio: set CSS dimensions to letterbox within the camera area
      // so the canvas display matches the buffer aspect ratio (no stretching).
      this._fitCanvasToContainer(modal.canvas, canvasW, canvasH);
    }

    // ── ResizeObserver — handle window/orientation changes ──────────────────
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => {
      if (!this.scene || !this.modal) return;
      const vw = this.modal.video.videoWidth || 640;
      const vh = this.modal.video.videoHeight || 480;
      const maxDim = 720;
      const scale = Math.min(1, maxDim / Math.max(vw, vh));
      const canvasW = Math.round(vw * scale);
      const canvasH = Math.round(vh * scale);
      this.modal.canvas.width = canvasW;
      this.modal.canvas.height = canvasH;
      this.scene.resize(canvasW, canvasH);
      this._fitCanvasToContainer(this.modal.canvas, canvasW, canvasH);
    });
    // Observe the container (not the canvas, since we're changing canvas size)
    this.resizeObserver.observe(modal.container);

    // ── Start the render/tracking loop ────────────────────────────────────────
    this.wasTracking = false;
    this.startTrackingLoop();

    // Camera + tracking infrastructure is now ready — fire session_started
    this.analytics.trackSessionStarted();

    if (this.triggerBtn) setTriggerButtonDisabled(this.triggerBtn, false);
  }

  /**
   * Close the try-on modal and stop the tracking loop.
   *
   * The tracker and scene are kept alive so re-opening is instant.
   * Safe to call multiple times.
   */
  async close(): Promise<void> {
    if (!this.isOpen) return;
    this.isOpen = false;

    // Reset to live mode for next open
    this.tryOnMode = 'live';

    // Stop the tracking loop
    this.stopTrackingLoop();

    // Stop camera stream but keep the manager warm
    this.camera?.stop();

    // Destroy scene + renderer so a fresh one binds to the new canvas on re-open.
    // Without this, the renderer stays bound to the old (removed) canvas element
    // and subsequent opens render to a detached DOM node.
    this.scene?.destroy();
    this.scene = new TryonScene();
    this.glasses?.destroy();
    this.glasses = new GlassesManager();
    this.materials?.destroy();
    this.materials = new MaterialsManager(this.scene);
    this.debugOverlay = null;

    // Destroy catalog slider
    this.catalog?.destroy();
    this.catalog = null;

    // Animate out and remove from Shadow DOM
    if (this.modal) {
      const { overlay } = this.modal;
      hideModal(overlay, () => {
        if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      }, 'ft-');
      this.modal = null;
    }

    if (this.triggerBtn) {
      setTriggerButtonDisabled(this.triggerBtn, false);
      // Return focus to the trigger button (WCAG 2.4.3 focus order)
      this.triggerBtn.focus();
    }

    const durationMs = this.openedAt !== null ? Date.now() - this.openedAt : 0;
    this.openedAt = null;
    this.bus.emit('close');
    this.analytics.trackWidgetClosed(durationMs);
  }

  /**
   * Switch the displayed product variant (KHR_materials_variants).
   *
   * Emits `variant-change` immediately so the UI can update. The scene swap is
   * a no-op when no model is loaded — the variant will be applied on next load.
   *
   * @param variantName - Name of the variant to activate.
   */
  switchVariant(variantName: string, materialOverrides?: Record<string, MeshMaterialOverride>): void {
    // Apply to scene if ready (no-op when no model is loaded)
    this.scene?.switchVariant(variantName);
    this.activeVariantName = variantName;
    // Apply per-mesh material overrides if provided
    if (materialOverrides && this.materials) {
      void this.materials.applyMeshOverrides(materialOverrides);
    }
    // Always emit — callers must receive the event to update their UI
    this.bus.emit('variant-change', { variantName });
    this.analytics.trackVariantChanged(variantName, this.config.productId);
  }

  /**
   * Capture the current try-on frame as a Blob.
   *
   * Composites the Three.js canvas (AR overlay) on top of the live camera feed
   * by drawing both into an off-screen canvas and calling `toBlob()`. Returns
   * `null` when the modal is not open or the canvas is unavailable.
   *
   * Also emits the `snapshot` widget event and fires the analytics tracker.
   *
   * @returns A PNG Blob of the composite frame, or `null`.
   */
  async snapshot(): Promise<Blob | null> {
    if (!this.modal) return null;

    return captureSnapshot({
      canvas: this.modal.canvas,
      renderFrame: () => this.scene?.render(),
      emitSnapshot: (productId) => this.bus.emit('snapshot', { productId }),
      trackSnapshot: (productId) => this.analytics.trackSnapshotTaken(productId),
      showToast: createSnapshotToastHandler(
        this.modal!.snapshotToast,
        'ft-',
        this.mediaPort ?? undefined,
      ),
      productId: this.config.productId,
    });
  }

  /**
   * Subscribe to widget events.
   *
   * @param event    - Event name.
   * @param callback - Handler function.
   */
  on(...args: Parameters<EventBus['on']>): void {
    this.bus.on(...args);
  }

  /**
   * Unsubscribe a previously registered event handler.
   *
   * @param event    - Event name.
   * @param callback - The exact function reference passed to `on()`.
   */
  off(...args: Parameters<EventBus['off']>): void {
    this.bus.off(...args);
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  /**
   * Tracks the GA4 callback currently registered so we can remove it if the
   * `analytics` attribute changes.
   */
  private ga4Callback: ReturnType<typeof createGA4Callback> | null = null;

  /**
   * Sync analytics adapters with the current config.
   *
   * Called from connectedCallback and whenever the `analytics` attribute
   * changes. Removes any stale GA4 adapter before registering a fresh one.
   *
   * Also registers the `onAnalytics` callback from config (programmatic API).
   */
  private applyAnalyticsConfig(): void {
    // Remove stale GA4 adapter
    if (this.ga4Callback) {
      this.analytics.removeCallback(this.ga4Callback);
      this.ga4Callback = null;
    }

    // Register new GA4 adapter if analyticsId is set
    if (this.config.analyticsId) {
      this.ga4Callback = createGA4Callback(this.config.analyticsId);
      this.analytics.addCallback(this.ga4Callback);
    }

    // Register onAnalytics callback from config (programmatic API)
    if (this.config.onAnalytics) {
      this.analytics.addCallback(this.config.onAnalytics);
    }
  }

  /**
   * Set CSS width/height on the canvas so it letterboxes within the camera area
   * while preserving the video's native aspect ratio (prevents stretching).
   */
  /**
   * Scale the canvas to fill the full container height, centered horizontally.
   * Uses a "cover" strategy: the canvas always spans 100% of the vertical
   * space; if the video is wider than the camera area, sides are cropped
   * symmetrically so the face stays centered.
   */
  private _fitCanvasToContainer(canvas: HTMLCanvasElement, bufferW: number, bufferH: number): void {
    const parent = canvas.parentElement;
    if (!parent) return;

    const containerW = parent.clientWidth * 0.65; // 65% camera area
    const containerH = parent.clientHeight;
    if (containerW <= 0 || containerH <= 0) return;

    const videoAspect = bufferW / bufferH;

    // Fill the full height; width follows from aspect ratio
    const cssH = containerH;
    const cssW = containerH * videoAspect;

    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;
    // Centre horizontally — sides crop symmetrically if cssW > containerW
    canvas.style.left = `${(containerW - cssW) / 2}px`;
    canvas.style.top = '0px';
  }

  private render(): void {
    // Inject scoped styles
    const styleEl = document.createElement('style');
    styleEl.textContent = STYLES as string;
    this.shadow.appendChild(styleEl);

    // Wrapper div — needed to scope CSS custom property overrides
    const wrapper = document.createElement('div');
    wrapper.className = 'ft-wrapper';
    this.applyTheme(wrapper);

    // Trigger button — label attribute is optional; omit key when absent
    const labelAttr = this.getAttribute('label');
    this.triggerBtn = createTriggerButton({
      ...(labelAttr !== null ? { label: labelAttr } : {}),
      onClick: () => void this.open(),
      cssPrefix: 'ft-',
    });
    wrapper.appendChild(this.triggerBtn);
    this.shadow.appendChild(wrapper);
  }

  private applyTheme(el: HTMLElement): void {
    el.setAttribute('data-theme', this.config.theme);
  }

  private applyFitCalibration(): void {
    this.scene?.setIpdScaleFactorOverride(this.resolveIpdScaleFactor());
  }

  private providePDLandmarks(callback: Parameters<PDLandmarkProvider>[0]): () => void {
    this.pdSampleCallback = callback;
    return () => {
      if (this.pdSampleCallback === callback) this.pdSampleCallback = null;
    };
  }

  private deliverPDLandmarks(landmarks: EyewearLandmarks, video: HTMLVideoElement): void {
    if (!this.pdSampleCallback || !landmarks.rawNormalizedLandmarks) return;
    const videoWidth = video.videoWidth || video.clientWidth || 0;
    const videoHeight = video.videoHeight || video.clientHeight || 0;
    if (videoWidth <= 0 || videoHeight <= 0) return;

    const shouldContinue = this.pdSampleCallback(
      landmarks.rawNormalizedLandmarks,
      videoWidth,
      videoHeight,
      landmarks.rotation.roll,
    );
    if (!shouldContinue) this.pdSampleCallback = null;
  }

  private handlePDMeasure(result: PDMeasurePayload): void {
    this.bus.emit('pd-measured', result);
    if (this.config.usePdCalibration) this.applyFitCalibration();
  }

  private resolveIpdScaleFactor(): number | null {
    if (this.config.ipdScaleFactor !== undefined) return this.config.ipdScaleFactor;
    if (!this.config.usePdCalibration) return null;

    const pdMm = this.readStoredPdMm();
    if (pdMm === null) return null;

    const referencePdMm = this.resolveReferencePdMm();
    return pdMm / referencePdMm;
  }

  private resolveReferencePdMm(): number {
    const referencePdMm = this.config.referencePdMm;
    return referencePdMm !== undefined && Number.isFinite(referencePdMm) && referencePdMm > 0
      ? referencePdMm
      : DEFAULT_REFERENCE_PD_MM;
  }

  private readStoredPdMm(): number | null {
    try {
      const value = window.localStorage.getItem(PD_STORAGE_KEY);
      if (value === null) return null;
      const pdMm = Number(value);
      if (!Number.isFinite(pdMm) || pdMm < PD_MIN_MM || pdMm > PD_MAX_MM) return null;
      return pdMm;
    } catch {
      return null;
    }
  }

  private collectAttributes(): Record<string, string> {
    const attrs: Record<string, string> = {};
    for (const name of OBSERVED_ATTRS) {
      const val = this.getAttribute(name);
      if (val !== null) attrs[name] = val;
    }
    return attrs;
  }

  /** Build the ProductManagerContext for delegating to product-manager.ts. */
  private _pmCtx(): ProductManagerContext {
    return {
      config: this.config,
      modal: this.modal,
      catalog: this.catalog,
      scene: this.scene,
      glasses: this.glasses,
      analytics: this.analytics,
      bus: this.bus,
      mediaPort: this.mediaPort,
      loadProduct: (id: string) => this.loadProduct(id),
    };
  }

  /**
   * Fetch a product from the API and load its 3-D model.
   */
  private async loadProduct(productId: string): Promise<void> {
    const requestGeneration = ++this.productLoadGeneration;
    return loadProductManaged(
      {
        ...this._pmCtx(),
        isLatestProductRequest: () => requestGeneration === this.productLoadGeneration,
      },
      productId,
    );
  }

  /**
   * Handle the add-to-cart button click.
   *
   * Emits the 'add-to-cart' event, attempts the platform cart bridge,
   * and shows visual feedback.
   */
  private _handleAddToCart(): void {
    if (!this.modal) return;
    const productId = this.config.productId;
    this.bus.emit('add-to-cart', { productId, variantName: this.activeVariantName });

    // Attempt platform cart bridge when a productId is available
    if (productId) {
      const bridgeOpts: CartBridgeOptions = {};
      if (this.config.cartUrl) bridgeOpts.cartUrl = this.config.cartUrl;
      if (this.config.cartPlatform) bridgeOpts.platform = this.config.cartPlatform;

      const cartReq = { productId };
      if (this.activeVariantName) {
        (cartReq as { productId: string; variantId: string }).variantId =
          this.activeVariantName;
      }

      void addToCart(cartReq, bridgeOpts).then((result) => {
        if (result.ok) {
          this.bus.emit('cart-success', {
            productId,
            variantId: this.activeVariantName,
            platform: result.platform,
          });
          this._cartToast.show(this.modal!.container, 'success', 'Added to cart');
        } else if (result.platform !== 'none') {
          // Only show error toast + emit event when a bridge was actually configured
          this.bus.emit('cart-error', {
            productId,
            variantId: this.activeVariantName,
            platform: result.platform,
            message: result.message,
          });
          this._cartToast.show(
            this.modal!.container,
            'error',
            `Couldn't add to cart${result.message ? `: ${result.message}` : ''}`,
          );
        }
        // platform === 'none' → no bridge configured; silently skip UI feedback
        // (the 'add-to-cart' event was already emitted for host-page handlers)
      });
    }

    // Visual feedback on the button — "Added!" state (always, regardless of bridge)
    const btn = this.modal.addToCartBtn;
    const originalText = btn.textContent ?? '';
    btn.classList.add('ft-add-to-cart-btn--added');
    btn.textContent = t(this.config.locale, 'cart.added');
    setTimeout(() => {
      btn.classList.remove('ft-add-to-cart-btn--added');
      btn.textContent = originalText;
    }, 2000);
  }

  /**
   * Fetch the full product catalog and populate the CatalogSlider.
   *
   * Non-fatal: catalog failure does not block the camera/AR experience.
   */
  private async _loadCatalog(): Promise<void> {
    return loadCatalogManaged(this._pmCtx());
  }

  /**
   * Switch between live camera mode and photo upload mode.
   *
   * Live → Photo: pauses the camera stream, hides the video, shows the upload area.
   * Photo → Live: resumes the camera stream, hides the photo/upload area, starts loop.
   */
  private async switchTryOnMode(mode: 'live' | 'photo'): Promise<void> {
    if (!this.modal || mode === this.tryOnMode) return;
    this.tryOnMode = mode;

    await switchModeDOM(this.modal, mode, {
      onPauseTracking: () => {
        this.stopTrackingLoop();
        this.camera?.stop();
      },
      onResumeCamera: async (video) => {
        if (this.camera) {
          try {
            await this.camera.start();
            video.srcObject = this.camera.stream;
          } catch {
            // Camera restart failed — silently fall back to empty feed
          }
        }
      },
      onStartTracking: () => {
        this.wasTracking = false;
        this.startTrackingLoop();
      },
    });
  }

  /**
   * Load an image File selected by the user, run face detection on it, and
   * render the glasses overlay as a single composited frame.
   */
  private loadPhotoFile(file: File): Promise<void> {
    if (!this.modal) return Promise.resolve();

    return loadPhotoFileManaged(this.modal, file, {
      onPhotoLoaded: (img) => {
        // Run face detection on the image
        if (this.tracker?.isReady && this.scene?.isInitialized) {
          // Pass the HTMLImageElement as ImageSource with a unique timestamp so
          // the MediaPipe video-mode landmarker accepts it without re-init.
          const landmarks = this.tracker.track(img as unknown as HTMLVideoElement);
          if (landmarks) {
            this.scene.updateFromLandmarks(landmarks);
            this.occluder?.update(landmarks);
            this.occluder?.setVisible(true);
          }
          this.scene.render();
        }
      },
      onTrackPhotoUploaded: () => {
        this.analytics.trackPhotoUploaded(this.config.productId);
      },
    });
  }

  /**
   * Start the per-frame tracking + render loop.
   *
   * Stops automatically when isOpen becomes false.
   */
  /**
   * Create or destroy the debug overlay based on the current config.debug flag.
   * Safe to call at any time — handles all states (widget closed, scene not init, etc.).
   */
  private async _syncDebugOverlay(): Promise<void> {
    const wantDebug = !!this.config.debug;

    // Update tracker debug flag
    if (this.tracker) {
      (this.tracker as MediaPipeTracker).setDebug(wantDebug);
    }

    if (wantDebug && !this.debugOverlay) {
      // Create overlay
      const threeScene = this.scene?.getScene();
      if (threeScene) {
        this.debugOverlay = new DebugOverlay();
        await this.debugOverlay.init(threeScene);
      }
    } else if (!wantDebug && this.debugOverlay) {
      // Destroy overlay
      const threeScene = this.scene?.getScene();
      this.debugOverlay.destroy(threeScene);
      this.debugOverlay = null;
    }
  }

  private startTrackingLoop(): void {
    /** Target ~30 FPS — halves MediaPipe calls on 60Hz displays. */
    const FRAME_INTERVAL_MS = 1000 / 30;
    // Deadline-based throttle: tracks the absolute timestamp of the next expected
    // frame slot rather than comparing elapsed delta. Initialised to 0 so the
    // very first rAF call always processes immediately.
    let nextFrameDeadline = 0;

    // Lazily initialise the perf recorder once per widget lifecycle.
    this.perf ??= new PerfRecorder();

    const loop = (now: DOMHighResTimeStamp) => {
      if (!this.isOpen) return;

      // Deadline-based throttle: process only when we've reached the next expected
      // frame slot. Comparing against an absolute deadline rather than elapsed delta
      // makes this immune to rAF jitter (e.g. the 33.2ms vs 33.3ms boundary miss
      // that was dropping effective rate from 30 to ~25 FPS on 60Hz displays).
      if (now < nextFrameDeadline) {
        this.trackingLoopId = requestAnimationFrame(loop);
        return;
      }
      // Advance the deadline by exactly one frame interval.
      // Math.max clamp: if the tab was backgrounded for several seconds, jump
      // forward to `now + FRAME_INTERVAL_MS` instead of scheduling a burst of
      // catch-up frames that would hammer MediaPipe.
      nextFrameDeadline = Math.max(nextFrameDeadline + FRAME_INTERVAL_MS, now + FRAME_INTERVAL_MS);

      // Use the modal's video element (in the DOM, visible, receiving frames)
      // rather than the camera's internal element which may not be in the DOM.
      // Check for WebGL context loss (Safari background tab recovery)
      if (this.scene?.isContextLost()) {
        if (!this.wasContextLost) {
          this.wasContextLost = true;
          if (this.modal) {
            this.modal.trackingBadge.textContent = 'Restoring\u2026';
            this.modal.trackingBadge.classList.add('ft-tracking-badge--lost');
            this.modal.trackingBadge.style.opacity = '1';
          }
        }
        this.trackingLoopId = requestAnimationFrame(loop);
        return;
      } else if (this.wasContextLost) {
        this.wasContextLost = false;
        if (this.modal) {
          this.modal.trackingBadge.style.opacity = '0';
          this.modal.trackingBadge.classList.remove('ft-tracking-badge--lost');
        }
      }

      // Mark frame start for perf instrumentation (after throttle + context-lost guards).
      this.perf?.onFrameStart(now);

      const video = this.modal?.video;
      if (video && video.readyState >= 2 && this.tracker?.isReady && this.scene?.isInitialized) {
        const landmarks = this.tracker.track(video);
        // Measure tracker latency regardless of whether a face was detected.
        this.perf?.onTrackerComplete();
        if (landmarks) {
          this.scene.updateFromLandmarks(landmarks);
          this.occluder?.update(landmarks);
          this.occluder?.setVisible(true);
          this.debugOverlay?.update(landmarks);
          this.debugOverlay?.setVisible(true);
          this.deliverPDLandmarks(landmarks, video);
          this.scene.render();
          this.perf?.onRenderComplete();

          if (!this.wasTracking) {
            this.wasTracking = true;
            if (this.config.debug) console.log('[forge-tryon] Face tracking started. Nose bridge:', landmarks.noseBridge, 'IPD:', Math.hypot(landmarks.rightPupil[0]-landmarks.leftPupil[0], landmarks.rightPupil[1]-landmarks.leftPupil[1]).toFixed(4));
            this.bus.emit('tracking-start');
            this.analytics.trackTrackingStarted();
            // Update tracking badge for screen readers + visual feedback
            if (this.modal) {
              this.modal.trackingBadge.textContent = 'Face detected'; // i18n handled by locale in badge init
              this.modal.trackingBadge.classList.remove('ft-tracking-badge--lost');
              this.modal.trackingBadge.style.opacity = '0';
            }
          }

          // Emit face-shape event when shape is first detected or changes
          if (landmarks.faceShape?.shape && landmarks.faceShape.shape !== this._lastFaceShape) {
            this._lastFaceShape = landmarks.faceShape.shape;
            this.bus.emit('face-shape', {
              shape: landmarks.faceShape.shape,
              confidence: landmarks.faceShape.confidence,
              recommended: landmarks.faceShape.recommended,
              measurements: landmarks.faceShape.measurements,
            });
          }
        } else {
          // No face detected — hide overlays, render last known frame
          this.occluder?.setVisible(false);
          this.debugOverlay?.setVisible(false);
          this.scene.render();
          this.perf?.onRenderComplete();

          if (this.wasTracking) {
            this.wasTracking = false;
            this._lastFaceShape = null;
            this.bus.emit('tracking-lost');
            this.analytics.trackTrackingLost();
            // Show tracking-lost badge
            if (this.modal) {
              this.modal.trackingBadge.textContent = 'Face not detected — look at the camera';
              this.modal.trackingBadge.classList.add('ft-tracking-badge--lost');
              this.modal.trackingBadge.style.opacity = '1';
            }
          }
        }
      } else if (this.scene?.isInitialized) {
        // Scene ready but tracker not yet initialised — render an empty frame
        this.scene.render();
        this.perf?.onRenderComplete();
      }

      this.trackingLoopId = requestAnimationFrame(loop);
    };

    this.trackingLoopId = requestAnimationFrame(loop);
  }

  /**
   * Cancel the tracking loop.
   */
  private stopTrackingLoop(): void {
    if (this.trackingLoopId !== null) {
      cancelAnimationFrame(this.trackingLoopId);
      this.trackingLoopId = null;
    }
    this.wasTracking = false;
  }

  /**
   * Full teardown — called from disconnectedCallback.
   *
   * Releases all GPU, WASM, and camera resources. The element is no longer
   * usable after this call.
   */
  private destroy(): void {
    // Close modal and stop loop if open
    if (this.isOpen) {
      this.isOpen = false;
      this.stopTrackingLoop();
      this.camera?.stop();
      if (this.modal) {
        const { overlay } = this.modal;
        if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
        this.modal = null;
      }
    }

    // Destroy catalog slider
    this.catalog?.destroy();
    this.catalog = null;

    // Disconnect ResizeObserver
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    // Clear cart toast timer if pending
    this._cartToast.dispose();

    // Destroy all heavy resources
    this.tracker?.destroy();
    this.tracker = null;

    const threeScene = this.scene?.getScene();
    this.occluder?.destroy(threeScene);
    this.occluder = null;

    this.debugOverlay?.destroy(threeScene);
    this.debugOverlay = null;

    this.scene?.destroy();
    this.scene = null;

    this.glasses?.destroy();
    this.glasses = null;

    this.materials?.destroy();
    this.materials = null;

    this.camera?.destroy();
    this.camera = null;

    // Reset perf recorder — releases ring-buffer memory.
    this.perf?.reset();
    this.perf = null;

    this.triggerBtn = null;
    this.bus.destroy();
  }
}
